import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import { showApiErrors } from "../../utils/helpers";
import {
    Calendar,
    Plus,
    Trash2,
    Users,
    Building2,
    Clock,
    Save,
    ArrowLeft,
    AlertCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const BulkSchedule = () => {
    const { requestWithAuth } = useAuth();
    const navigate = useNavigate();

    // Data State
    const [staffMembers, setStaffMembers] = useState([]);
    const [branches, setBranches] = useState([]);

    // Form State
    const [date, setDate] = useState("");
    const [entries, setEntries] = useState([
        { id: Date.now(), user: "", branch: "", session: "" }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Constants
    const sessions = ["morning", "evening"];

    useEffect(() => {
        fetchUsers();
        getBranches();
    }, []);

    const fetchUsers = async () => {
        try {
            const data = await requestWithAuth(
                `/users/accounts/?paginated=false&status=Active`,
                "GET"
            );
            setStaffMembers(data || []);
        } catch (error) {
            console.error("Error fetching users data:", error);
            toast.error("Failed to load staff members");
        }
    };

    const getBranches = async () => {
        try {
            const data = await requestWithAuth(
                `/company/branches/?paginated=false&status=Active&branch_type=retail`,
                "GET"
            );
            setBranches(data || []);
        } catch (error) {
            console.error("Error fetching branches:", error);
            toast.error("Failed to load branches");
        }
    };

    const handleAddRow = () => {
        setEntries([
            ...entries,
            { id: Date.now(), user: "", branch: "", session: "" }
        ]);
    };

    const handleRemoveRow = (id) => {
        if (entries.length === 1) {
            toast.warning("You must have at least one entry.");
            return;
        }
        setEntries(entries.filter(entry => entry.id !== id));
    };

    const handleEntryChange = (id, field, value) => {
        setEntries(entries.map(entry =>
            entry.id === id ? { ...entry, [field]: value } : entry
        ));
    };

    const validateEntries = () => {
        if (!date) {
            toast.error("Please select a date first.");
            return false;
        }

        // Check for empty fields
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            if (!entry.user || !entry.branch || !entry.session) {
                toast.error(`Please fill in all fields for row ${i + 1}`);
                return false;
            }
        }

        // Check for duplicates (Staff + Session)
        // A staff member cannot have more than one schedule for the same session (regardless of branch).
        const uniqueKeys = new Set();
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const key = `${entry.user}-${entry.session}`;
            if (uniqueKeys.has(key)) {
                toast.error(`Duplicate entry found for row ${i + 1}: Staff member already scheduled for this session.`);
                return false;
            }
            uniqueKeys.add(key);
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateEntries()) return;

        Swal.fire({
            title: "Submit Schedules?",
            text: `You are about to create ${entries.length} schedule(s) for ${date}.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, submit!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsSubmitting(true);
                let successCount = 0;
                let errors = [];

                // Execute Sequentially to handle errors or partial success gracefully
                for (const entry of entries) {
                    const payload = {
                        user: entry.user,
                        branch: entry.branch,
                        session: entry.session,
                        date: date,
                        is_active: true
                    };

                    try {
                        await requestWithAuth(`/users/staff-schedule/`, "POST", payload);
                        successCount++;
                    } catch (error) {
                        const errorData = error.response?.data;
                        let errorMsg = "Unknown error";

                        if (errorData?.data?.errors?.length > 0) {
                            errorMsg = errorData.data.errors.join(", ");
                        } else if (errorData?.message) {
                            errorMsg = errorData.message;
                        } else if (errorData?.detail) {
                            errorMsg = errorData.detail;
                        } else if (error.message) {
                            errorMsg = error.message;
                        }

                        // Try to get a user friendly name
                        const staffName = staffMembers.find(s => s.id === parseInt(entry.user))?.full_name || "Unknown Staff";
                        errors.push(`<b>${staffName}</b>: ${errorMsg}`);
                    }
                }

                setIsSubmitting(false);

                if (errors.length === 0) {
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: `${successCount} schedules added successfully!`,
                    }).then(() => {
                        // Reset or Redirect
                        navigate("/administration/staff-schedule");
                    });
                } else {
                    Swal.fire({
                        icon: "warning",
                        title: "Partial Success",
                        html: `Added ${successCount} schedules.<br/><br/>Failed: ${errors.length}<br/><div class="text-left text-xs bg-gray-100 p-2 rounded max-h-40 overflow-auto">${errors.map(e => `<div>• ${e}</div>`).join('')}</div>`,
                    });
                }
            }
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        <span>Back</span>
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <Users size={32} className="text-blue-600" />
                        Bulk Schedule Creation
                    </h1>
                    <p className="text-gray-500 mt-2">
                        Create multiple staff schedules for a specific date in one go.
                    </p>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">

                    {/* Global Date Selection */}
                    <div className="mb-8 max-w-sm">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Calendar size={16} className="text-gray-500" />
                            Select Schedule Date
                            <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Entries List */}
                    <div className="space-y-4 mb-8">
                        <div className="flex items-center justify-between border-b pb-2 mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Schedule Entries</h3>
                            <span className="text-sm text-gray-500">{entries.length} entr{entries.length !== 1 ? 'y' : 'ies'}</span>
                        </div>

                        {entries.map((entry, index) => (
                            <div
                                key={entry.id}
                                className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-gray-50 p-4 rounded-lg border border-gray-200 relative group animate-in fade-in slide-in-from-bottom-2 duration-300"
                            >
                                {/* Remove Button for Mobile/Desktop */}
                                <button
                                    onClick={() => handleRemoveRow(entry.id)}
                                    className="absolute -top-2 -right-2 bg-white text-red-500 p-1.5 rounded-full shadow border border-gray-200 hover:bg-red-50 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all z-10"
                                    title="Remove row"
                                >
                                    <Trash2 size={16} />
                                </button>

                                {/* Number Indicator */}
                                <div className="hidden md:flex md:col-span-1 items-center justify-center font-bold text-gray-300 text-xl">
                                    #{index + 1}
                                </div>

                                {/* Staff Select */}
                                <div className="md:col-span-4">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Staff Member <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Users size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <select
                                            value={entry.user}
                                            onChange={(e) => handleEntryChange(entry.id, 'user', e.target.value)}
                                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value="">Select Staff</option>
                                            {staffMembers.map((staff) => (
                                                <option key={staff.id} value={staff.id}>
                                                    {staff.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Branch Select */}
                                <div className="md:col-span-4">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Branch <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Building2 size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <select
                                            value={entry.branch}
                                            onChange={(e) => handleEntryChange(entry.id, 'branch', e.target.value)}
                                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value="">Select Branch</option>
                                            {branches
                                                .filter(b => b.is_stores === false)
                                                .map((branch) => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Session Select */}
                                <div className="md:col-span-3">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Session <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Clock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <select
                                            value={entry.session}
                                            onChange={(e) => handleEntryChange(entry.id, 'session', e.target.value)}
                                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm capitalize"
                                        >
                                            <option value="">Select Session</option>
                                            {sessions.map((session) => (
                                                <option key={session} value={session}>
                                                    {session}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col md:flex-row gap-4 justify-between pt-4 border-t border-gray-200">
                        <button
                            onClick={handleAddRow}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                        >
                            <Plus size={20} />
                            Add Another Row
                        </button>

                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg shadow-blue-500/30 ${isSubmitting ? 'opacity-75 cursor-wait' : ''}`}
                            >
                                {isSubmitting ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                ) : (
                                    <Save size={20} />
                                )}
                                <span>{isSubmitting ? 'Submitting...' : 'Submit Schedules'}</span>
                            </button>
                        </div>
                    </div>

                    {/* Hint Box */}
                    <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
                        <AlertCircle className="text-blue-500 shrink-0 mt-0.5" size={20} />
                        <div className="text-sm text-blue-800 space-y-1">
                            <p className="font-semibold">Bulk Schedule Tips:</p>
                            <ul className="list-disc pl-4 space-y-1">
                                <li>A staff member can be scheduled for multiple branches in different sessions.</li>
                                <li>A staff member cannot be scheduled for the same session more than once.</li>
                                <li>Make sure to select the correct date for all entries.</li>
                            </ul>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default BulkSchedule;